import { ApplicationConfig, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { appRoutes } from './app.routes';
import { provideMapboxGL } from 'ngx-mapbox-gl';
import { environment } from 'src/environments/environment';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { providePrimeNG } from 'primeng/config';
import MyPreset from './theme/app-theme';
import {
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalInterceptor,
    MsalGuardConfiguration,
    MsalInterceptorConfiguration,
} from '@azure/msal-angular';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { apiConfig, b2cPolicies } from './b2c-config';
import { initializeMsalSession } from '@core/auth/msal-session';

export function loggerCallback(logLevel: LogLevel, message: string) {
    console.log(`[MSAL] ${message}`);
}

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.b2cClientId,
            authority: b2cPolicies.authorities.signUpSignIn.authority,
            redirectUri: '/',
            postLogoutRedirectUri: '/signed-out',
            knownAuthorities: [b2cPolicies.authorityDomain],
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
        },
        system: {
            loggerOptions: {
                loggerCallback,
                logLevel: LogLevel.Warning,
                piiLoggingEnabled: false,
            },
        },
    });
}
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    const apiBase = apiConfig.uri.replace(/\/+$/, '');
    protectedResourceMap.set(apiBase, apiConfig.scopes);
    protectedResourceMap.set(`${apiBase}/*`, apiConfig.scopes);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
        strictMatching: true,
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...apiConfig.scopes],
        },
        loginFailedRoute: '/login',
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideAppInitializer(() => {
            const msalService = inject(MsalService);
            inject(MsalBroadcastService);
            return initializeMsalSession(msalService, apiConfig.scopes);
        }),
        provideZonelessChangeDetection(),
        provideRouter(appRoutes),
        provideHttpClient(withInterceptorsFromDi()),
        provideMapboxGL({
            accessToken: environment.mapboxAccessToken,
        }),
        providePrimeNG({
            license:
                'eyJpZCI6IjQ3MWY5Y2QwLTViNjktNGZmMC1hODJmLWQxZjNhOWMyYTQ5NSIsInByb2R1Y3QiOiJwcmltZXVpIiwidGllciI6ImNvbW11bml0eSIsInR5cGUiOiJkZXYiLCJpYXQiOjE3OTAzNzUwNTIsImV4cCI6MTgyMTkxMTA1Mn0.pKGfMsk5e8mYWPTg7phUSn9ZgsRRyEEQgpMElupguAugyeazisbua6hMWdO5iagSyOMLOijOCbEPad5LMobvCA',
            theme: {
                preset: MyPreset,
                options: {
                    darkModeSelector: '.theme-dark',
                },
            },
        }),
        provideStoreDevtools({
            maxAge: 30,
            logOnly: environment.production,
            connectInZone: true,
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
    ],
};
